import { useCallback, useState } from 'react';

import { SelectMode, SelectionState, TRowModel } from '../types';
import { getSelectionState, getRowIds, getRowSelections, getUpdatedSelection } from '../utils';

type Props<Row extends TRowModel<Row>> = {
  data: TRowModel<Row>[];
  selectedRows: string[] | 'ALL' | undefined;
  isRowDisabled?: (row: Row) => boolean;
  onRowSelectChange?: (value: 'ALL' | SelectionState) => void;
};

export const useRowSelection = <Row extends TRowModel<Row>>({
  data,
  selectedRows,
  isRowDisabled,
  onRowSelectChange,
}: Props<Row>) => {
  const [tableSelectMode, setTableSelectMode] = useState<SelectionState>({
    bulkSelect: false,
    unselected: [],
    selected: [],
  });

  const { allRowIds, subRowIds } = getRowIds<Row>(data, isRowDisabled);

  const handleToggleAllRows = useCallback(
    (value: SelectMode) => {
      if (!onRowSelectChange) {
        return;
      }

      const state = {
        bulkSelect: value === 'ALL',
        selected: [],
        unselected: [],
      };

      onRowSelectChange(state.bulkSelect ? 'ALL' : state);
      setTableSelectMode(state);
    },
    [onRowSelectChange],
  );

  const selectionState = getSelectionState(selectedRows, allRowIds, tableSelectMode);
  const isBulkSelected = !!Object.keys(selectionState).length && tableSelectMode.bulkSelect;

  const handleToggleRow = useCallback(
    (value: SelectMode, record: Row, parentRow: Row | undefined) => {
      if (!onRowSelectChange) {
        return;
      }

      const updatedSelections = getUpdatedSelection(record, value, selectionState, parentRow);
      const selectedIds = Object.keys(updatedSelections).filter(
        (key) => updatedSelections[key] === 'ALL',
      );
      const updatedDeselectedIds = Object.keys(updatedSelections).filter(
        (id) => updatedSelections[id] === 'NONE',
      );

      // array of object with row and sub rows selections
      const selected = getRowSelections(data, selectedIds);
      const unselected = isBulkSelected ? updatedDeselectedIds : [];

      const selectedMainRowIds = selected.map(({ rowId }) => rowId);
      const selectedSubRowIds = selected
        .flatMap(({ selectedSubrows }) => selectedSubrows)
        .filter(Boolean);

      const selectionMode: SelectionState = {
        bulkSelect: isBulkSelected,
        selected,
        unselected,
        unselectedSubRowsCount: subRowIds.size - selectedSubRowIds.length,
        unselectedMainRowsCount: data.length - selectedMainRowIds.length,
      };

      onRowSelectChange(selectionMode);
      setTableSelectMode(selectionMode);
    },
    [onRowSelectChange, selectionState, isBulkSelected, data, subRowIds.size],
  );

  return {
    handleToggleAllRows,
    handleToggleRow,
    selectionState,
  };
};
